import {
  Table,
  Button,
  Input,
  Space,
  Dropdown,
  DatePicker,
  notification,
  Skeleton,
  Flex,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetUsersWithParams } from "../../../../hooks/users";
import { buildUserColumns } from "./user.table.columns.builder";
import { buildStatusMenu } from "./user.table.filters.builder";
import { debounce } from "lodash";

const UserTable = () => {
  const navigate = useNavigate();

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [dateRange, setDateRange] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const {
    data: resultData,
    isLoading,
    refetch,
  } = useGetUsersWithParams(
    {
      page: pagination.current - 1,
      size: pagination.pageSize,
      enabled: statusFilter,
      username: searchText,
      lastLoggedInAfter: dateRange?.[0]?.toISOString(),
      lastLoggedInBefore: dateRange?.[1]?.toISOString(),
    },
    {
      keepPreviousData: true,
      referchOnWindowFocus: false,
    }
  );

  const users = resultData?._embedded?.users || [];
  const totalUsers = resultData?.page?.totalElements || 0;

  const [notificationApi, notificationCtx] = notification.useNotification();

  const handleTableChange = (pagination) => {
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchText(value);
        setPagination((prev) => ({ ...prev, current: 1 }));
      }, 400),
    []
  );

  const handleSearch = (e) => debouncedSearch(e.target.value);

  const handleStatusSelect = ({ key }) => {
    setStatusFilter(key === "all" ? null : key === "active");
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleDateFilter = (dates) => {
    setDateRange(dates ?? []);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleView = (user) =>
    navigate(`/manage/auth/users/${user.uid}/details`);
  const handleEdit = (user) => navigate(`/manage/auth/users/${user.uid}/edit`);
  const handleAdd = () => navigate("/manage/auth/users/add-new-user");

  const handleDelete = async (uid) => {
    notificationApi.info({ message: `User ${uid} deleted.` });
    refetch();
  };

  const columns = useMemo(
    () =>
      buildUserColumns({
        onView: handleView,
        onEdit: handleEdit,
        onDelete: handleDelete,
        notify: notificationApi,
      }),
    []
  );

  return (
    <div style={{ background: "#fff", borderRadius: 8 }}>
      {notificationCtx}
      <Flex
        flex="row"
        style={{
          padding: 16,
          borderBottom: "1px solid #f0f0f0",
          justifyContent: "space-between",
        }}
      >
        <Space>
          <Input
            value={searchText}
            placeholder="Search users..."
            onChange={handleSearch}
            prefix={<SearchOutlined />}
            style={{ width: 300 }}
            allowClear
          />
          <Dropdown
            menu={buildStatusMenu(handleStatusSelect)}
            trigger={["click"]}
          >
            <Button icon={<FilterOutlined />}>Status</Button>
          </Dropdown>
          <DatePicker.RangePicker onChange={handleDateFilter} />
        </Space>
        <Button type="primary" onClick={handleAdd} icon={<PlusOutlined />}>
          Add User
        </Button>
      </Flex>
      <Skeleton active loading={isLoading && !resultData}>
        <Table
          columns={columns}
          rowKey={(record) => record.uid}
          dataSource={users}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: totalUsers,
            showSizeChanger: true,
            showTotal: (total, range) => (
              <span>
                <b>{range[0]} </b> – <b>{range[1]}</b> of <b>{total}</b> users
              </span>
            ),
          }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
        />
      </Skeleton>
    </div>
  );
};

export default UserTable;
